import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import get from 'lodash/get';

import { filterSensitive } from './filter-sensitive';
import { getFullName } from './get-full-name';
import { isQualificationRejection } from '../../participant/redux/result/util.result';

const { bugsnagApiKey, releaseStage } = window.beagly;

// info - https://developer.mozilla.org/en-US/docs/Web/HTTP/Status#client_error_responses
export function bugsnagReportableStatus(status) {
  const parsedStatus = parseInt(status, 10);
  if (parsedStatus >= 500 || !parsedStatus) {
    return true;
  }
  return new Set([400, 405, 406, 408, 413, 415, 429, 431]).has(parsedStatus);
}

export const isAxiosNetworkError = (error) =>
  Boolean(error && error.isAxiosError) && !error.response;

export const isReportableError = (error) => {
  if (isAxiosNetworkError(error)) {
    return false;
  }
  return bugsnagReportableStatus(get(error, 'response.status'));
};

export const bugsnagNotify = (error, options = {}) => {
  if (!bugsnagApiKey) {
    throw error;
  }
  Bugsnag.notify(error, (event) => {
    if ('metadata' in options) {
      event.addMetadata('metadata', options.metadata);
    }
  });
};

const toMeaningfulMessage = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const message = value.trim();
  if (!message || message.startsWith('<') || !/[\p{L}\p{N}]/u.test(message)) {
    return null;
  }
  return message;
};

export const getApiErrorMessage = (error) => {
  const data = get(error, 'response.data');
  return (
    toMeaningfulMessage(get(data, 'message')) ||
    toMeaningfulMessage(get(data, 'errors[0].message')) ||
    toMeaningfulMessage(data)
  );
};

export const getApiErrorGroupingHash = (error) => {
  const type = get(error, 'config.type');
  const status = get(error, 'response.status');
  if (!type || !status) {
    return null;
  }
  return `${type}:${status}`;
};

const OWN_BUNDLE_PATH = '/packs/';
const REJECTION_VALUE_MAX_LENGTH = 200;

export const isThirdPartyRejection = (event) => {
  if (get(event, '_handledState.severityReason.type') !== 'unhandledPromiseRejection') {
    return false;
  }
  const frames = get(event, 'errors[0].stacktrace') || [];
  return !frames.some((frame) => String(get(frame, 'file', '')).includes(OWN_BUNDLE_PATH));
};

const stringifyRejection = (value) => {
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Error || typeof get(value, 'message') === 'string') {
    return value.message;
  }
  try {
    const json = JSON.stringify(value);
    return json === undefined ? String(value) : json;
  } catch (e) {
    return String(value);
  }
};

export const getRejectionValue = (event) =>
  stringifyRejection(get(event, 'originalError')).slice(0, REJECTION_VALUE_MAX_LENGTH);

export const getThirdPartyRejectionGroupingHash = (errorClass, value) =>
  `third-party-rejection:${errorClass}:${value.replace(/\d+/g, '#')}`;

const downgradeToWarning = (event) => {
  Object.assign(event, {
    unhandled: false,
    severity: 'warning',
    severityReason: {
      type: 'handledException',
    },
  });
};

export const bugsnagOnError = (event) => {
  const originalError = get(event, 'originalError');
  if (isQualificationRejection(originalError)) {
    return false;
  }

  if (isThirdPartyRejection(event)) {
    downgradeToWarning(event);
    const rejectionError = get(event, 'errors[0]');
    if (rejectionError) {
      const value = getRejectionValue(event);
      rejectionError.errorMessage = value;
      Object.assign(event, {
        groupingHash: getThirdPartyRejectionGroupingHash(rejectionError.errorClass, value),
      });
    }
    return event;
  }

  const shouldReportByStatus = isReportableError(originalError);
  const name = get(originalError, 'name');
  const shouldReportByName = name !== 'NotAnError';
  const shouldReport = shouldReportByStatus && shouldReportByName;

  if (!shouldReport) {
    downgradeToWarning(event);
  }

  const apiErrorMessage = getApiErrorMessage(originalError);
  const eventError = get(event, 'errors[0]');
  if (apiErrorMessage && eventError) {
    const status = get(originalError, 'response.status');
    eventError.errorMessage = `[${status}] ${apiErrorMessage}`;
    if (typeof event.addMetadata === 'function') {
      event.addMetadata('apiError', {
        message: apiErrorMessage,
        status,
        method: get(originalError, 'config.method'),
        url: get(originalError, 'config.url'),
      });
    }
    const groupingHash = getApiErrorGroupingHash(originalError);
    if (groupingHash) {
      Object.assign(event, { groupingHash });
    }
  }

  return event;
};

if (bugsnagApiKey) {
  const endpointsPrefix = process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '';
  Bugsnag.start({
    apiKey: bugsnagApiKey,
    appVersion: process.env.BUILD_ID || process.env.VERSION,
    ...(releaseStage ? { releaseStage } : {}),
    onError: bugsnagOnError,
    plugins: [new BugsnagPluginReact()],
    endpoints: {
      sessions: `${endpointsPrefix}/bugsnag/session`,
      notify: `${endpointsPrefix}/bugsnag/notify`,
    },
  });
}

export const bugsnagLeaveBreadcrumb = (name, unsecureMetadata = {}) => {
  if (bugsnagApiKey) {
    const metadata = filterSensitive(unsecureMetadata);
    Bugsnag.leaveBreadcrumb(name, metadata);
  }
};

export const bugsnagSetUser = (user) => {
  if (user && bugsnagApiKey) {
    const { email, id, firstName, lastName } = user;
    const fullName = getFullName({ firstName, lastName });
    Bugsnag.setUser(id, email, fullName);
  }
};

/**
 * @see docs/worklog/maintenance/20093-investigate-facebook-auth-errors/rfc-frontend.md
 */
